/**
 * Phase 5 D-05 — Zod input + output schemas for all 8 assistant tools.
 *
 * The 8 logical tools (per D-05) register as 9 internal tool names because
 * `get_points` splits into `get_points_balance` + `get_points_transactions`.
 *
 * Input schemas are converted to Anthropic tool `input_schema` via
 * `z.toJSONSchema()` (Zod v4 native — do NOT use the deprecated
 * `zod-to-json-schema` package per RESEARCH §"State of the Art").
 *
 * Output schemas all carry `truncated: boolean` where AI-06 bounded output
 * caps apply, so Claude knows when results were clipped.
 */
import { z } from 'zod';
import { MEMORY_TEXT_MAX, MemoryKindSchema } from './memories.schemas';

/** Loose UUID regex — accepts both v4 and v7 (36-char canonical form). */
const UuidSchema = z.string().regex(/^[0-9a-f-]{36}$/i, 'invalid_uuid');

// ── Tool Input Schemas (Anthropic tool input_schema source via z.toJSONSchema) ──

export const GetWeatherInputSchema = z.object({
  /** Number of hours ahead to include in forecast. Default 24, max 24 (D-05). */
  hours: z.number().int().min(1).max(24).default(24).describe('Antall timer fram i tid (maks 24).'),
});

export const GetMembersInputSchema = z.object({
  /**
   * No inputs. The roster is small (a household) and always returned whole, so
   * there is nothing worth filtering on — and a filter the handler ignored is
   * exactly the class of bug that `hours` and `unlocked_only` were.
   */
});

export const GetChoresInputSchema = z.object({
  /** Filter by status. Default: all. */
  status: z
    .enum(['available', 'claimed', 'completed', 'approved'])
    .optional()
    .describe('Filtrer på status. completed=ferdig av barn (venter godkjenning), approved=godkjent av forelder. Standard: alle.'),
  /** Filter by member. Only admins can query other members. */
  member_id: UuidSchema.optional().describe('Filtrer på medlem (kun foreldre kan spørre om andre).'),
  /** Max results to return. Default 20, max 50 (D-05). */
  limit: z.number().int().min(1).max(50).default(20),
});

export const GetPointsBalanceInputSchema = z.object({
  /** Default: the calling member. */
  member_id: UuidSchema.optional().describe('Standard: innloggede medlem.'),
});

export const GetPointsTransactionsInputSchema = z.object({
  /** Only own history for children (POINT-02). */
  member_id: UuidSchema.optional().describe('Kun egen historikk for barn (POINT-02).'),
  /** Max results. Default 20, max 100 (D-05). */
  limit: z.number().int().min(1).max(100).default(20),
});

export const GetCalendarEventsInputSchema = z.object({
  /** Oslo-dato YYYY-MM-DD (fra dagens start) eller ISO-tidspunkt. Standard: i dag. */
  from: z
    .string()
    .describe('Startdato YYYY-MM-DD (fra midnatt, norsk tid) eller ISO-tidspunkt. Standard: i dag.')
    .optional(),
  /** Oslo-dato YYYY-MM-DD er inklusiv (hele dagen tas med). Maks 90 dager. Standard: from + 7 dager. */
  to: z
    .string()
    .describe(
      'Siste dato YYYY-MM-DD, inklusiv: hele dagen tas med. For én enkelt dag, sett from og to til samme dato. ' +
        'Et ISO-tidspunkt er eksklusiv slutt. Maks 90 dager fra from. Standard: from + 7 dager.',
    )
    .optional(),
  /** Filter by member. */
  member_id: UuidSchema.optional(),
  /** Max results. Default 20, max 100 (D-05). */
  limit: z.number().int().min(1).max(100).default(20),
});

export const GetShoppingListInputSchema = z.object({
  /** Filter by checked/unchecked. Default: unchecked. */
  checked: z.boolean().optional().describe('Filtrer på krysset/ukrysset. Standard: ukrysset.'),
});

export const GetStreaksInputSchema = z.object({
  /** Default: the calling member. */
  member_id: UuidSchema.optional().describe('Standard: innloggede medlem.'),
});

export const GetAchievementsInputSchema = z.object({
  /** Filter to specific member. */
  member_id: UuidSchema.optional(),
  /** If true, only return unlocked achievements. Default false. */
  unlocked_only: z.boolean().default(false),
});

export const AddToShoppingListInputSchema = z.object({
  /** The item text to add. Min 1, max 120 chars (D-11). */
  text: z.string().min(1).max(120).describe('Vare som skal legges til.'),
});

// ── AI-21 fase 2 (#195): bruksmønstre og måltidsplanen ─────────────────────────

/** Områdene `get_family_patterns` kan oppsummere (memory-plan.md, fase 2). */
export const FAMILY_PATTERN_AREAS = ['shopping', 'meals', 'calendar', 'chores'] as const;
export type FamilyPatternArea = (typeof FAMILY_PATTERN_AREAS)[number];

export const GetFamilyPatternsInputSchema = z.object({
  area: z
    .enum(FAMILY_PATTERN_AREAS)
    .describe(
      'shopping=varer som går igjen, meals=retter, calendar=ukedager og faste avtaler, chores=oppgaver per barn: tatt og fullført (utløp spores ikke).',
    ),
  /** Vinduet bakover i tid, 4–26 uker (memory-plan.md). Standard 8. */
  weeks: z.number().int().min(4).max(26).default(8).describe('Antall uker bakover (4–26). Standard 8.'),
});

/** `YYYY-MM-DD` som også er en ekte dato — «2026-13-45» stopper her, ikke som tool_failed. */
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'invalid_date')
  .refine((s) => {
    const d = new Date(`${s}T12:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(s);
  }, 'invalid_date');

export const GetMealsInputSchema = z
  .object({
    /** Standard: mandag denne uka (Oslo), som `GET /api/meals`. */
    from: IsoDateSchema.optional().describe('Første dato YYYY-MM-DD. Standard: mandag denne uka.'),
    /** Inklusiv. Maks 31 dager fra `from`. Standard: søndag samme uke. */
    to: IsoDateSchema.optional().describe('Siste dato YYYY-MM-DD, inklusiv. Maks 31 dager. Standard: søndag samme uke.'),
  })
  .refine((v) => v.from === undefined || v.to === undefined || v.from <= v.to, {
    message: 'to_before_from',
    path: ['to'],
  });
// ── Hukommelse (AI-21, #192; docs/chat-agent/memory-plan.md 1d) ─────────────
// Tilbys bare når familiens hukommelse er på, og aldri på vision-runder —
// tekst i et bilde skal aldri bli et notat (#21). Skrivepolicyen (hvem kan
// skrive hva om hvem) håndheves i memory.service, ikke her.

/** Positivt notatnummer (`#12`) fra `<familienotater>` eller et tidligere `remember`-svar. */
const MemoryRefInput = z.number().int().positive();

export const RememberInputSchema = z.object({
  text: z
    .string()
    .min(1)
    .max(MEMORY_TEXT_MAX)
    .describe('Én opplysning på norsk, kort og selvstendig. Aldri mat, allergi, kosthold eller helse.'),
  about: z
    .union([z.enum(['self', 'family']), UuidSchema])
    .optional()
    .describe("Hvem notatet handler om: 'self' (den du snakker med, standard), 'family' (hele familien) eller en member_id."),
  kind: MemoryKindSchema.describe('fact=opplysning, preference=ønske, routine=fast avtale, style=hvordan du skal svare denne personen.'),
  expires_on: IsoDateSchema.optional().describe('Siste dag notatet gjelder (YYYY-MM-DD), for midlertidige ting. Utelat for varige.'),
  replaces_ref: MemoryRefInput.optional().describe('Nummeret på et notat dette erstatter (oppdatering).'),
});
export type RememberInput = z.infer<typeof RememberInputSchema>;

export const ForgetInputSchema = z.object({
  ref: MemoryRefInput.describe('Nummeret på notatet som skal glemmes, f.eks. 12 for #12.'),
});
export type ForgetInput = z.infer<typeof ForgetInputSchema>;

// ── Tool Output Schemas ────────────────────────────────────────────────────────
// All bounded outputs include `truncated: boolean` (AI-06).

export const ToolOutputBaseSchema = z.object({
  /** True when the result was clipped to the tool's `limit` cap (AI-06). */
  truncated: z.boolean().default(false),
});

/**
 * Generic tool output — passthrough for detailed response shapes that are
 * defined per-service. Concrete shapes validated at the service layer.
 */
export const GenericToolOutputSchema = ToolOutputBaseSchema.passthrough();

// ── TOOL_NAMES ───────────────────────────────────────────────────────────────
// Every tool name the registry can expose, all-roles and admin-only alike.
//
// This previously claimed to be "the canonical 9-entry list" while omitting the
// three Fase 6 draft tools, so `ToolName` was a silently incomplete union. It
// had no non-test consumers, which is why nothing broke — but anything typed
// against it would have been wrong. Keep it in sync with tools.registry.ts —
// apps/api tools.registry.test.ts asserts the two agree (the check cannot live
// here, since shared must not import from the API package).

export const TOOL_NAMES = [
  // Read tools, available to every role.
  'get_weather',
  'get_members',
  'get_chores',
  'get_points_balance',
  'get_points_transactions',
  'get_calendar_events',
  'get_shopping_list',
  'get_streaks',
  'get_achievements',
  // Write tool, available to every role.
  'add_to_shopping_list',
  // Fase 6 vision draft tools — admin only (see getToolsForMember).
  'create_chore_draft',
  'create_calendar_event_draft',
  'create_meal_draft',
  // AI-21 fase 2 (#195) — lesing, alle roller.
  'get_family_patterns',
  'get_meals',
  // Hukommelse (#192) — bare når familiens hukommelse er på, aldri på vision-runder.
  'remember',
  'forget',
] as const;

export type ToolName = (typeof TOOL_NAMES)[number];
